import { useEffect, useState } from "react";
import {
  Linkedin,
  Loader2,
  Check,
  Unlink,
  Download,
  AlertCircle,
  Clock,
  ShieldCheck,
  RefreshCw,
  History,
  Plug,
  PlugZap,
  Ban,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  LINKEDIN_CLIENT_ID,
  buildLinkedInAuthUrl,
  getMyLinkedInConnection,
  disconnectLinkedIn,
  refreshLinkedInToken,
  getMyLinkedInAuditLog,
  type LinkedInAuditEvent,
} from "@/lib/linkedin";

type LinkedInProfile = {
  sub?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  picture?: string;
};

type Connection = {
  linkedin_sub: string;
  profile: LinkedInProfile;
  scope: string | null;
  expires_at: string;
  connected_at: string;
};

const REFRESH_THRESHOLD_DAYS = 7;

function friendlyError(raw?: string | null): string {
  if (!raw) return "Something went wrong. Please try connecting again.";
  const msg = raw.toLowerCase();
  if (msg.includes("user_cancelled") || msg.includes("user_denied") || msg.includes("access_denied"))
    return "You cancelled the LinkedIn sign-in. Try again whenever you're ready.";
  if (msg.includes("state mismatch") || msg.includes("invalid_state"))
    return "Security check failed. Please retry — this usually resolves itself on a second attempt.";
  if (msg.includes("redirect_uri") || msg.includes("redirect uri"))
    return "Redirect URL mismatch. Our team has been notified — please try again shortly.";
  if (msg.includes("linkedin_token_exchange_failed") || msg.includes("invalid_client"))
    return "LinkedIn rejected the sign-in. Please retry; if it persists, contact support.";
  if (msg.includes("linkedin_profile_fetch_failed"))
    return "We couldn't read your LinkedIn profile. Check that you granted name + email permissions.";
  if (msg.includes("unauthorized"))
    return "Your session expired. Please sign in again, then reconnect LinkedIn.";
  if (msg.includes("locked"))
    return "Your identity is locked after the first assessment. Only your avatar can be imported.";
  if (msg.includes("no_refresh_token"))
    return "LinkedIn didn't issue a refresh token for this app. Please reconnect when the token expires.";
  if (msg.includes("refresh_rejected"))
    return "LinkedIn rejected the token refresh. Please reconnect your account.";
  if (msg.includes("network") || msg.includes("failed to fetch"))
    return "Network error. Check your connection and retry.";
  return raw;
}

function formatRelative(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function daysUntil(iso?: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

const eventMeta: Record<
  LinkedInAuditEvent["event"],
  { label: string; icon: typeof Plug; tone: string }
> = {
  connected: { label: "Connected", icon: Plug, tone: "text-primary" },
  disconnected: { label: "Disconnected", icon: Unlink, tone: "text-muted-foreground" },
  refreshed: { label: "Token refreshed", icon: RefreshCw, tone: "text-primary" },
  revoked: { label: "Token revoked", icon: Ban, tone: "text-muted-foreground" },
  imported: { label: "Profile imported", icon: Download, tone: "text-primary" },
  error: { label: "Error", icon: AlertCircle, tone: "text-destructive" },
};

export default function LinkedInPanel({ onProfileImported }: { onProfileImported?: () => void }) {
  const { user } = useAuth();
  const [conn, setConn] = useState<Connection | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<null | "connect" | "disconnect" | "import" | "refresh">(null);
  const [error, setError] = useState<string | null>(null);
  const [audit, setAudit] = useState<LinkedInAuditEvent[]>([]);
  const [showAudit, setShowAudit] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [autoRefreshNote, setAutoRefreshNote] = useState<string | null>(null);

  async function loadAudit() {
    const rows = await getMyLinkedInAuditLog(15);
    setAudit(rows);
  }

  async function refresh() {
    setError(null);
    try {
      const data = (await getMyLinkedInConnection()) as Connection | null;
      setConn(data);
    } catch (e) {
      setError(friendlyError((e as Error)?.message));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    loadAudit();
  }, []);

  // Auto-refresh near expiry — silent attempt; if it succeeds, update UI, else surface a note.
  useEffect(() => {
    if (!conn?.expires_at) return;
    const days = daysUntil(conn.expires_at);
    if (days === null) return;
    if (days > REFRESH_THRESHOLD_DAYS) return;
    if (busy) return;

    let cancelled = false;
    (async () => {
      const res = await refreshLinkedInToken();
      if (cancelled) return;
      if (res.ok === true) {
        setAutoRefreshNote(`Token auto-refreshed · valid until ${formatRelative(res.expires_at)}`);
        await refresh();
        await loadAudit();
      } else {
        const reason = res.reason;
        if (reason === "no_refresh_token") {
          setAutoRefreshNote(
            "Auto-refresh unavailable for this LinkedIn app. Please reconnect before the token expires.",
          );
        } else {
          setAutoRefreshNote(`Auto-refresh failed: ${friendlyError(reason)}`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // run once per loaded connection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conn?.expires_at]);

  function connect() {
    setBusy("connect");
    setError(null);
    try {
      window.location.href = buildLinkedInAuthUrl(LINKEDIN_CLIENT_ID, "/settings");
    } catch (e) {
      setBusy(null);
      setError(friendlyError((e as Error)?.message));
    }
  }

  async function manualRefresh() {
    setBusy("refresh");
    setError(null);
    const res = await refreshLinkedInToken();
    setBusy(null);
    if (res.ok === true) {
      toast.success("LinkedIn token refreshed");
      setAutoRefreshNote(null);
      await refresh();
      await loadAudit();
      return;
    }
    const m = friendlyError(res.reason);
    setError(m);
    toast.error(m);
  }

  async function performDisconnect() {
    setConfirmOpen(false);
    setBusy("disconnect");
    setError(null);
    const { error: err, revoked } = await disconnectLinkedIn();
    setBusy(null);
    if (err) {
      const m = friendlyError(err);
      setError(m);
      toast.error(m);
      await loadAudit();
      return;
    }
    setConn(null);
    setAutoRefreshNote(null);
    if (revoked === "failure") {
      toast.warning("Disconnected locally — LinkedIn server-side revoke didn't confirm.");
    } else {
      toast.success("LinkedIn disconnected & token revoked");
    }
    await loadAudit();
  }

  async function importProfile() {
    if (!user || !conn) return;
    setBusy("import");
    setError(null);
    const patch: { full_name?: string; avatar_url?: string } = {};
    if (conn.profile.name) patch.full_name = conn.profile.name;
    if (conn.profile.picture) patch.avatar_url = conn.profile.picture;
    const { error: err } = await supabase.from("profiles").update(patch).eq("id", user.id);
    if (err) {
      if (err.message?.toLowerCase().includes("locked")) {
        if (conn.profile.picture) {
          const { error: avErr } = await supabase
            .from("profiles")
            .update({ avatar_url: conn.profile.picture })
            .eq("id", user.id);
          setBusy(null);
          if (!avErr) {
            toast.success("Avatar imported from LinkedIn");
            onProfileImported?.();
            return;
          }
          const m = friendlyError(avErr.message);
          setError(m);
          toast.error(m);
          return;
        }
      }
      setBusy(null);
      const m = friendlyError(err.message);
      setError(m);
      toast.error(m);
      return;
    }
    setBusy(null);
    toast.success("Profile imported from LinkedIn");
    onProfileImported?.();
  }

  const canPost = conn?.scope?.includes("w_member_social");
  const scopes = (conn?.scope ?? "").split(/\s+/).filter(Boolean);
  const expiresInDays = daysUntil(conn?.expires_at);
  const tokenExpiring = expiresInDays !== null && expiresInDays <= REFRESH_THRESHOLD_DAYS && expiresInDays > 0;
  const tokenExpired = expiresInDays !== null && expiresInDays <= 0;

  return (
    <Card>
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-semibold flex items-center gap-2">
              <Linkedin className="h-5 w-5 text-[#0A66C2]" /> LinkedIn
            </h2>
            <p className="text-xs text-muted-foreground mt-1 max-w-md">
              Connect your LinkedIn account to import your profile and (once approved) share milestones to your feed.
            </p>
          </div>
          {conn ? (
            <Badge
              variant="outline"
              className={
                tokenExpired
                  ? "gap-1.5 border-destructive/40 text-destructive"
                  : tokenExpiring
                    ? "gap-1.5 border-amber-500/50 text-amber-600"
                    : "gap-1.5 border-primary/30 text-primary"
              }
            >
              {tokenExpired ? <AlertCircle className="h-3 w-3" /> : <Check className="h-3 w-3" />}
              {tokenExpired ? "Reconnect" : tokenExpiring ? "Expiring soon" : "Connected"}
            </Badge>
          ) : (
            <Badge variant="outline" className="gap-1.5 text-muted-foreground">
              Not connected
            </Badge>
          )}
        </div>

        {error && (
          <Alert variant="destructive" className="border-destructive/30">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">{error}</AlertDescription>
          </Alert>
        )}

        {autoRefreshNote && (
          <Alert className="border-primary/30">
            <PlugZap className="h-4 w-4 text-primary" />
            <AlertDescription className="text-xs">{autoRefreshNote}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking your LinkedIn connection…
          </div>
        ) : conn ? (
          <>
            <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/40 p-3">
              {conn.profile.picture ? (
                <img
                  src={conn.profile.picture}
                  alt={conn.profile.name ?? "LinkedIn"}
                  className="h-11 w-11 rounded-full object-cover ring-2 ring-background"
                />
              ) : (
                <div className="h-11 w-11 rounded-full bg-[#0A66C2]/15 flex items-center justify-center">
                  <Linkedin className="h-5 w-5 text-[#0A66C2]" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm truncate">{conn.profile.name ?? "LinkedIn user"}</p>
                <p className="text-xs text-muted-foreground truncate">{conn.profile.email ?? "—"}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="rounded-lg border border-border/60 bg-background p-3">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                  <Clock className="h-3 w-3" /> Connected
                </div>
                <div className="mt-1 text-sm font-medium">{formatRelative(conn.connected_at) || "—"}</div>
              </div>
              <div className="rounded-lg border border-border/60 bg-background p-3">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                  <ShieldCheck className="h-3 w-3" /> Token expires
                </div>
                <div
                  className={`mt-1 text-sm font-medium ${
                    tokenExpired ? "text-destructive" : tokenExpiring ? "text-amber-600" : ""
                  }`}
                >
                  {formatRelative(conn.expires_at) || "—"}
                  {expiresInDays !== null && !tokenExpired && (
                    <span className="text-muted-foreground font-normal"> · {expiresInDays}d</span>
                  )}
                </div>
              </div>
            </div>

            {scopes.length > 0 && (
              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Permissions granted
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {scopes.map((s) => (
                    <Badge key={s} variant="secondary" className="text-[10px] font-mono">
                      {s}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {tokenExpired && (
              <Alert className="border-destructive/30">
                <AlertCircle className="h-4 w-4 text-destructive" />
                <AlertDescription className="text-xs">
                  Your LinkedIn token has expired. Reconnect to keep your profile in sync.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={importProfile}
                disabled={busy !== null || tokenExpired}
                variant="default"
                size="sm"
                className="gap-1.5"
              >
                {busy === "import" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Import name + avatar
              </Button>
              {tokenExpired ? (
                <Button onClick={connect} disabled={busy !== null} size="sm" variant="outline" className="gap-1.5">
                  {busy === "connect" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Reconnect
                </Button>
              ) : (
                <Button onClick={manualRefresh} disabled={busy !== null} size="sm" variant="ghost" className="gap-1.5">
                  {busy === "refresh" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Refresh token
                </Button>
              )}
              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={busy !== null}
                variant="outline"
                size="sm"
                className="gap-1.5 text-destructive hover:text-destructive"
              >
                {busy === "disconnect" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />}
                Disconnect
              </Button>
            </div>

            {!canPost && (
              <p className="text-[11px] text-muted-foreground">
                Posting to your LinkedIn feed requires the <code className="font-mono">w_member_social</code> scope, which needs LinkedIn app review. Sign-in and profile import work today.
              </p>
            )}
          </>
        ) : (
          <div className="space-y-3">
            <Button
              onClick={connect}
              disabled={busy !== null}
              className="gap-2 bg-[#0A66C2] text-white hover:bg-[#0A66C2]/90"
            >
              {busy === "connect" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Linkedin className="h-4 w-4" />}
              Connect LinkedIn
            </Button>
            <p className="text-[11px] text-muted-foreground">
              We'll request your name, email, and profile photo. You can disconnect anytime — disconnecting revokes the token on LinkedIn's servers too.
            </p>
          </div>
        )}

        {/* Audit log */}
        <div className="pt-2 border-t border-border/60">
          <button
            type="button"
            onClick={() => setShowAudit((v) => !v)}
            className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            <History className="h-3.5 w-3.5" />
            Connection history {audit.length > 0 && `(${audit.length})`}
            <span className="ml-1 text-[10px]">{showAudit ? "▲" : "▼"}</span>
          </button>

          {showAudit && (
            <div className="mt-3">
              {audit.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No LinkedIn activity recorded yet.</p>
              ) : (
                <ul className="divide-y divide-border/60 rounded-lg border border-border/60 overflow-hidden">
                  {audit.map((e) => {
                    const meta = eventMeta[e.event];
                    const Icon = meta.icon;
                    return (
                      <li key={e.id} className="flex items-start gap-3 px-3 py-2.5 bg-background">
                        <Icon
                          className={`h-4 w-4 mt-0.5 shrink-0 ${
                            e.status === "failure" ? "text-destructive" : meta.tone
                          }`}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-medium">{meta.label}</span>
                            {e.status === "failure" && (
                              <Badge variant="outline" className="text-[9px] border-destructive/30 text-destructive">
                                failed
                              </Badge>
                            )}
                          </div>
                          {e.detail && (
                            <p className="text-[11px] text-muted-foreground mt-0.5 break-words">{e.detail}</p>
                          )}
                          <p className="text-[10px] text-muted-foreground/70 mt-0.5">{formatDateTime(e.created_at)}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </CardContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect LinkedIn?</AlertDialogTitle>
            <AlertDialogDescription>
              This will revoke the access token on LinkedIn's servers and remove the connection from your account. You can reconnect any time, but you'll need to re-authorize the app.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === "disconnect"}>Keep connected</AlertDialogCancel>
            <AlertDialogAction
              onClick={performDisconnect}
              disabled={busy === "disconnect"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy === "disconnect" ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              ) : (
                <Unlink className="h-4 w-4 mr-1.5" />
              )}
              Disconnect & revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
